// apps/office/src/components/LabourSalaryModal.jsx
import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import pb from '../lib/pocketbaseClient';
import { X, DollarSign, Calendar, TrendingUp, TrendingDown } from 'lucide-react';

export default function LabourSalaryModal({ open, onClose, labour, vendors, sites, onSave }) {
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [attendanceData, setAttendanceData] = useState([]);
  const [salaryData, setSalaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNote, setPaymentNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && labour) {
      fetchData();
    }
  }, [open, labour, selectedMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const monthStart = `${selectedMonth}-01`;
      const monthEnd = `${selectedMonth}-${new Date(parseInt(selectedMonth.split('-')[1]), parseInt(selectedMonth.split('-')[0]), 0).getDate()}`;

      // Fetch attendance
      const attendance = await pb.collection('labour_attendance').getFullList({
        filter: `labour="${labour.id}" && date >= "${monthStart}" && date <= "${monthEnd}"`,
      });
      setAttendanceData(attendance);

      // Calculate salary
      const present = attendance.filter(r => r.status === 'present').length;
      const halfDay = attendance.filter(r => r.status === 'halfday').length;
      const totalDays = present + (halfDay * 0.5);
      const dailyRate = labour.dailyRate || 0;
      const totalSalary = totalDays * dailyRate;

      setSalaryData({
        present,
        halfDay,
        absent: attendance.length - present - halfDay,
        totalDays,
        totalSalary,
        dailyRate,
      });

      // Fetch transactions
      const trans = await pb.collection('labour_salary_transactions').getFullList({
        filter: `labour="${labour.id}"`,
        sort: '-created',
      });
      setTransactions(trans);

    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const data = {
        labour: labour.id,
        amount: parseFloat(paymentAmount),
        type: 'payment',
        note: paymentNote || 'Salary payment',
        date: new Date().toISOString().split('T')[0],
      };

      await pb.collection('labour_salary_transactions').create(data);
      toast.success('Payment recorded successfully');
      setPaymentAmount('');
      setPaymentNote('');
      fetchData();
      onSave();
    } catch (error) {
      setError(error.message || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const getTotalPaid = () => {
    return transactions
      .filter(t => t.type === 'payment')
      .reduce((sum, t) => sum + (t.amount || 0), 0);
  };

  const getTotalDue = () => {
    const totalPaid = getTotalPaid();
    return (salaryData?.totalSalary || 0) - totalPaid;
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-amber-500" />
              Salary Details
            </h2>
            <p className="text-sm text-slate-500">{labour?.name} - Monthly Salary</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm mb-4 border border-red-200">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {/* Month Selector */}
          <div className="flex items-center gap-4 bg-slate-50 p-3 rounded-lg">
            <Calendar className="w-5 h-5 text-slate-400" />
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-400"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              className="ml-auto"
            >
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-8 h-8 border-4 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              {/* Salary Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500">Present</p>
                    <p className="text-2xl font-bold text-emerald-600">{salaryData?.present || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500">Half Day</p>
                    <p className="text-2xl font-bold text-yellow-600">{salaryData?.halfDay || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500">Absent</p>
                    <p className="text-2xl font-bold text-red-600">{salaryData?.absent || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500">Daily Rate</p>
                    <p className="text-2xl font-bold text-slate-800">Rs.{salaryData?.dailyRate || 0}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Salary Calculation */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-lg border border-amber-200">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-xs text-slate-500">Total Working Days</p>
                    <p className="text-xl font-bold text-slate-700">{salaryData?.totalDays?.toFixed(1) || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Total Salary</p>
                    <p className="text-xl font-bold text-emerald-600">Rs.{salaryData?.totalSalary?.toFixed(2) || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Total Paid</p>
                    <p className="text-xl font-bold text-amber-600">Rs.{getTotalPaid().toFixed(2) || 0}</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-amber-200 text-center">
                  <p className="text-sm font-medium">
                    Due Amount: 
                    <span className={cn(
                      "ml-2 text-lg font-bold",
                      getTotalDue() > 0 ? "text-red-600" : "text-green-600"
                    )}>
                      {getTotalDue() > 0 ? '+' : ''}Rs.{getTotalDue().toFixed(2)}
                    </span>
                  </p>
                </div>
              </div>

              {/* Payment Form */}
              <div className="bg-white p-4 rounded-lg border border-slate-200">
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Record Payment</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Amount (Rs.)</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-slate-600 mb-1">Note</label>
                    <Input
                      value={paymentNote}
                      onChange={(e) => setPaymentNote(e.target.value)}
                      placeholder="Payment note..."
                    />
                  </div>
                </div>
                <Button
                  onClick={handlePayment}
                  disabled={submitting}
                  className="mt-3 bg-amber-500 hover:bg-amber-600 text-white"
                >
                  {submitting ? 'Processing...' : 'Record Payment'}
                </Button>
              </div>

              {/* Transaction History */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Transaction History</h4>
                <div className="max-h-48 overflow-y-auto">
                  {transactions.length === 0 ? (
                    <p className="text-center text-slate-400 text-sm py-4">No transactions yet</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Date</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Type</th>
                          <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Amount</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Note</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {transactions.map((t) => (
                          <tr key={t.id} className="hover:bg-slate-50">
                            <td className="px-3 py-2 text-xs">{formatDate(t.date)}</td>
                            <td className="px-3 py-2">
                              <Badge variant={t.type === 'payment' ? 'success' : 'warning'}>
                                {t.type}
                              </Badge>
                            </td>
                            <td className="px-3 py-2 text-right font-medium">
                              {t.type === 'payment' ? '-' : '+'}Rs.{t.amount?.toFixed(2)}
                            </td>
                            <td className="px-3 py-2 text-xs text-slate-500">{t.note || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}